import React, { useState, useRef, useEffect } from 'react';
import styles from './PainPointsSection.module.css';
import {
  CloseRounded,
  CheckRounded,
  ArrowForwardRounded,
  EventBusyOutlined,
  NotificationsActiveOutlined,
  ChatBubbleOutlineRounded,
  AutoAwesomeOutlined,
  AccountBalanceWalletOutlined,
  TrendingUpOutlined,
  DescriptionOutlined,
  CloudDoneOutlined,
  PeopleOutlined,
  AccessTimeOutlined,
  FolderOutlined,
  CalendarMonthOutlined,
  AlarmOutlined,
  ChatOutlined,
  AttachMoneyOutlined,
  ArticleOutlined,
} from '@mui/icons-material';
import { trackCtaClick } from '../../../../utils/analytics';
import OwnNumberNote from '../shared/OwnNumberNote';

const painSolutionPairs = [
  {
    problemIcon: <EventBusyOutlined />,
    problem: 'Faltas e horários vagos de última hora',
    solutionIcon: <NotificationsActiveOutlined />,
    solution: 'Confirmações com mensagem pronta e lista de espera',
  },
  {
    problemIcon: <ChatBubbleOutlineRounded />,
    problem: 'Horas respondendo mensagens e confirmando horários',
    solutionIcon: <AutoAwesomeOutlined />,
    solution: 'Comunicação organizada e automatizada*',
  },
  {
    problemIcon: <AccountBalanceWalletOutlined />,
    problem: 'Financeiro em planilhas e notas emitidas à mão',
    solutionIcon: <TrendingUpOutlined />,
    solution: 'Pagamentos, notas fiscais e indicadores em um só lugar',
  },
  {
    problemIcon: <DescriptionOutlined />,
    problem: 'Documentos e informações espalhadas',
    solutionIcon: <CloudDoneOutlined />,
    solution: 'Prontuários e documentos centralizados',
  },
];

const painPairs = [
  {
    problemIcon: <EventBusyOutlined />,
    problemLabel: 'Faltas de última hora',
    solutionIcon: <NotificationsActiveOutlined />,
    solutionLabel: 'Confirmações com mensagem pronta e lista de espera',
    problemColor: '#ff3383',
    solutionColor: '#5a80fb',
  },
  {
    problemIcon: <ChatBubbleOutlineRounded />,
    problemLabel: 'Horas confirmando horários',
    solutionIcon: <AutoAwesomeOutlined />,
    solutionLabel: 'Comunicação organizada e automatizada*',
    problemColor: '#ff3383',
    solutionColor: '#5a80fb',
  },
  {
    problemIcon: <AccountBalanceWalletOutlined />,
    problemLabel: 'Financeiro em planilhas',
    solutionIcon: <TrendingUpOutlined />,
    solutionLabel: 'Pagamentos, notas e indicadores juntos',
    problemColor: '#ff3383',
    solutionColor: '#5a80fb',
  },
  {
    problemIcon: <DescriptionOutlined />,
    problemLabel: 'Documentos espalhados',
    solutionIcon: <CloudDoneOutlined />,
    solutionLabel: 'Prontuários e documentos centralizados',
    problemColor: '#ff3383',
    solutionColor: '#5a80fb',
  },
];

const metrics = [
  {
    icon: <PeopleOutlined />,
    value: 'Menos faltas',
    label: 'com confirmações prontas ou automáticas*',
  },
  {
    icon: <AccessTimeOutlined />,
    value: 'Mais tempo para atender',
    label: 'e menos tarefas repetitivas',
  },
  {
    icon: <AutoAwesomeOutlined />,
    value: 'Visão completa do seu trabalho',
    label: 'para decisões mais seguras',
  },
  {
    icon: <FolderOutlined />,
    value: 'Dados protegidos',
    label: 'com segurança e privacidade',
  },
];

function PainPointsSection({ onScrollToBeta }) {
  const [activeCard, setActiveCard] = useState(0);
  const carouselRef = useRef(null);

  const handleCarouselScroll = () => {
    if (!carouselRef.current) return;
    const track = carouselRef.current;
    const firstCard = track.querySelector('[class*="mobileCarouselCard"]') || track.children[0];
    if (!firstCard) return;
    const cardWidth = firstCard.offsetWidth + 12;
    const index = Math.round(track.scrollLeft / cardWidth);
    setActiveCard(Math.min(Math.max(index, 0), 3)); // 4 cards = índice 0-3
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!carouselRef.current) return;
      const track = carouselRef.current;
      const firstCard = track.querySelector('[class*="mobileCarouselCard"]') || track.children[0];
      if (!firstCard) return;
      const cardWidth = firstCard.offsetWidth + 12; // largura real + gap
      const { scrollLeft, scrollWidth, offsetWidth } = track;
      const maxScroll = scrollWidth - offsetWidth;

      if (scrollLeft >= maxScroll - 20) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        setActiveCard(0);
      } else {
        const nextScroll = scrollLeft + cardWidth;
        track.scrollTo({ left: nextScroll, behavior: 'smooth' });
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const onCtaClick = () => {
    trackCtaClick({ placement: 'pain_points_mobile_cta' });
    onScrollToBeta();
  };

  return (
    <section id="beneficios" className={styles.painPointsSection}>

      {/* ── DESKTOP: conteúdo original ── */}
      <div className={`${styles.sectionContainer} ${styles.desktopOnly}`}>

        {/* Header */}
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>O que hoje toma o seu tempo</span>
          <h2 className={styles.sectionTitle}>
            Tudo que hoje toma tempo<br className={styles.titleBreak} /> da sua rotina
          </h2>
          <p className={styles.sectionSubtitle}>
            Confirmar horários, procurar informações, controlar pagamentos, emitir documentos. A Gabriela cuida dessa parte para você se dedicar ao atendimento.
          </p>
        </div>

        {/* Grid de pares problema → solução */}
        <div className={styles.pairsGrid}>
          {painSolutionPairs.map((pair, index) => (
            <div key={index} className={styles.pairCard}>

              {/* Lado do problema */}
              <div className={styles.problemSide}>
                <div className={styles.iconWrapper}>
                  <div className={styles.problemIconCircle}>
                    {pair.problemIcon}
                  </div>
                  <span className={styles.problemBadge}>
                    <CloseRounded style={{ fontSize: 10 }} />
                  </span>
                </div>
                <div className={styles.problemContent}>
                  <span className={styles.problemText}>{pair.problem}</span>
                </div>
              </div>

              {/* Seta */}
              <span className={styles.arrow} aria-hidden="true">
                <ArrowForwardRounded style={{ fontSize: 16 }} />
              </span>

              {/* Lado da solução */}
              <div className={styles.solutionSide}>
                <div className={styles.iconWrapper}>
                  <div className={styles.solutionIconCircle}>
                    {pair.solutionIcon}
                  </div>
                  <span className={styles.solutionBadge}>
                    <CheckRounded style={{ fontSize: 10 }} />
                  </span>
                </div>
                <div className={styles.solutionContent}>
                  <span className={styles.solutionText}>{pair.solution}</span>
                </div>
              </div>

            </div>
          ))}
        </div>

        {/* Faixa de benefícios */}
        <div className={styles.metricsStrip}>
          {metrics.map((metric, index) => (
            <React.Fragment key={index}>
              <div className={styles.metricItem}>
                <span className={styles.metricIcon}>{metric.icon}</span>
                <span className={styles.metricValue}>{metric.value}</span>
                <span className={styles.metricLabel}>{metric.label}</span>
              </div>
              {index < metrics.length - 1 && (
                <div className={styles.metricDivider} aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>

        <OwnNumberNote className={styles.sectionNote} />

        {/* CTA */}
        <div className={styles.sectionCta}>
          <button
            className={styles.ctaButton}
            onClick={() => {
              trackCtaClick({ placement: 'pain_points_cta' });
              onScrollToBeta();
            }}
          >
            Organizar minha rotina
          </button>
          <p className={styles.ctaSupport}>Sem cartão de crédito&nbsp;•&nbsp;Cancele quando quiser</p>
        </div>

      </div>

      {/* MOBILE: Carousel de dores */}
      <div className={styles.mobileCarouselSection}>
        <div className={styles.mobileCarouselHeader}>
          <h2 className={styles.mobileCarouselTitle}>
            Tudo que hoje<br />toma tempo<br />da sua rotina
          </h2>
          <p className={styles.mobileCarouselSubtitle}>
            Confirmar horários, procurar informações, controlar pagamentos, emitir documentos. A Gabriela cuida dessa parte para você se dedicar ao atendimento.
          </p>
        </div>

        <div
          className={styles.mobileCarouselTrack}
          ref={carouselRef}
          onScroll={handleCarouselScroll}
        >
          {painPairs.map((pair, index) => (
            <div key={index} className={styles.mobileCarouselCard}>
              {/* Lado do problema */}
              <div className={styles.mobileCardProblem}>
                <div className={styles.mobileCardIconWrapper} style={{ background: pair.problemColor + '20' }}>
                  <span className={styles.mobileCardIcon} style={{ color: pair.problemColor }}>
                    {pair.problemIcon}
                  </span>
                </div>
                <div className={styles.mobileCardArrow}>→</div>
                <div className={styles.mobileCardProblemLabel}>
                  {pair.problemLabel}
                </div>
              </div>

              {/* Lado da solução */}
              <div className={styles.mobileCardSolution}>
                <div className={styles.mobileCardIconWrapper} style={{ background: pair.solutionColor + '20' }}>
                  <span className={styles.mobileCardIcon} style={{ color: pair.solutionColor }}>
                    {pair.solutionIcon}
                  </span>
                </div>
                <div className={styles.mobileCardSolutionLabel}>
                  {pair.solutionLabel}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.mobileCarouselDots}>
          {painPairs.map((_, i) => (
            <div
              key={i}
              className={`${styles.mobileCarouselDot} ${i === activeCard ? styles.mobileCarouselDotActive : ''}`}
            />
          ))}
        </div>

        <OwnNumberNote className={styles.mobileSectionNote} />

        <button className={styles.mobileCarouselCta} onClick={onCtaClick}>
          Ver todas as soluções
        </button>
      </div>

    </section>
  );
}

export default PainPointsSection;
